/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { motion } from "framer-motion";
import { useRouter } from "next/router";
import { default as React, useEffect } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import { UserAtom } from "../../atoms/UserAtom";
import { ICategoryType } from "../../generated/Api";
import { FilterUserAtom } from "../../pages/users";
import { ITag } from "../../types/TagTypes";
import IconBerserkerMail from "../Icon";
import AddUser from "../Modal/container/AddUser";
import { useModal } from "../Modal/Modal";
import SearchInput from "../SearchInput";
import StyledButton from "../StyledButton";
import UpdateBillingModal from "../UpdateBillingModal";

const Container = styled.div`
  width: 100%;
  height: fit-content;
  border-bottom: 1px solid #cccccc;
  display: flex;
  justify-content: space-between;
  padding: 15px;
  align-items: center;
  padding-left: 25px;
  background-color: #ffffff;
`;

const Buttons = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  height: 35px;
  display: flex;
`;

const StyledButtonWithIcon = styled(StyledButton)`
  margin-right: 15px;
  padding: 10px 14px;
  justify-content: space-between;

  > div {
    margin-right: 7px;
  }
`;

export const CUSTOM_TAG = (n: number): ITag => ({
  name: `+${n} more`,
  _id: "-1",
  category: "user",
  userUid: "",
  type: ICategoryType.User,
  createdAt: new Date(),
  categoryId: "",
});

export const Input = styled(motion.input)`
  height: 35px;
  border: none;
  color: "#2F2F2F";
  background-color: "#FFFFFF";
  padding-left: 40px;

  border-radius: 5px;

  &::placeholder {
    font-size: 14px;
    letter-spacing: 0.16px;
    font-family: "Source Sans Pro", sans-serif !important;
    color: "#B1BBC6";
  }

  font-size: 14px;
  font-family: "Source Sans Pro", sans-serif !important;

  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
`;

const CloseIconContainer = styled.div`
  position: absolute;
  top: 12px;
  right: 10px;
  cursor: pointer;
`;

export default function Header({
  updateUsers,
  externalUsersCount,
}: {
  updateUsers: () => void;
  externalUsersCount: number;
}): JSX.Element {
  const modal = useModal();
  const user = useRecoilValue(UserAtom);
  const [filterUser, setFilterUser] = useRecoilState(FilterUserAtom);
  const router = useRouter();

  useEffect(() => {
    if (router.query.search) {
      let search = router.query.search as string;
      setFilterUser(search.toLowerCase().trim());
    }
  }, [router.query.search]);

  return (
    <Container>
      <Buttons>
        <SearchInput
          value={filterUser}
          handleChange={(evt) => setFilterUser(evt)}
          placeholder="Search for users"
          css={css`
            width: 220px;
          `}
        />

        <div
          css={css`
            display: flex;
            position: relative;
          `}
        >
          <StyledButtonWithIcon
            disable={!user?.billing?.active}
            onClick={() => {
              // Check if the user has enough external users to create a new one

              if ((user?.billing?.externalUsers || 0) > externalUsersCount) {
                modal.pushModal(
                  <AddUser updateUsers={updateUsers} />,
                  "Add new external user"
                );
              } else {
                // If the user doesn't have enough users, CTA
                modal.pushModal(
                  <UpdateBillingModal />,
                  "Buy more external users in order to create a new one"
                );
              }
            }}
          >
            <IconBerserkerMail
              name="plus"
              width={12}
              height={12}
              color="#fff"
            />
            Add New External User
          </StyledButtonWithIcon>
        </div>
      </Buttons>
    </Container>
  );
}
